"use client";

import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Info, Lightbulb, Rocket, Shield, Star, Users } from "lucide-react";

export default function ModalTextOnlyExample() {
  const [open, setOpen] = React.useState(true);
  React.useEffect(() => {
    const original = document.body.style.overflow;
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = original;
    }
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  const features = [
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "Bank-grade encryption and compliance certifications protect your data"
    },
    {
      icon: Rocket,
      title: "Lightning Fast",
      description: "Optimised performance ensures your team works efficiently without delays"
    },
    {
      icon: Users,
      title: "Team Collaboration",
      description: "Real-time collaboration tools keep everyone aligned and productive"
    },
    {
      icon: Star,
      title: "Smart Automation",
      description: "Smart features automate repetitive tasks and reduce manual work"
    }
  ];

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)}>Open Welcome Modal</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-7xl! w-7xl! h-screen bg-transparent shadow-none border-0 p-0 flex items-center justify-center">
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[90vh] shadow-2xl border border-white/10 flex flex-col relative">
            {/* Header */}
            <div className="sticky top-0 z-10 p-6 pb-4 border-b border-white/10 bg-card/70 backdrop-blur-xl supports-[backdrop-filter]:bg-card/60">
              <h2 className="text-3xl font-bold text-foreground">Welcome to Your New Workspace</h2>
              <p className="mt-2 text-muted-foreground">You're all set up! Here's what makes your experience exceptional</p>
              
              <div className="mt-3 flex items-center gap-4 text-sm">
                <Badge variant="outline">Setup Complete</Badge>
                <Badge variant="secondary">Ready to Go</Badge>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 flex-1 space-y-6 pb-2">
              {/* Welcome Message */}
              <Card className="bg-white/5 border-white/20">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Congratulations!
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-foreground leading-relaxed">
                    Your workspace has been successfully configured and is ready for use. We've set up everything based on your preferences, including your team structure, document types, and security settings.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    You now have access to powerful tools designed to streamline your workflow, enhance collaboration, and ensure compliance across all your operations.
                  </p>
                </CardContent>
              </Card>

              {/* Key Features */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-semibold">What's Available to You</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {features.map((feature, index) => {
                    const Icon = feature.icon;
                    return (
                      <Card key={index} className="bg-white/5 border-white/20 hover:bg-white/10 transition-colors">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-primary/10 text-primary">
                              <Icon className="h-5 w-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-sm mb-1">{feature.title}</h4>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {feature.description}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>

              {/* Getting Started */}
              <Card className="bg-white/5 border-white/20">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Info className="h-5 w-5" />
                    Getting Started
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 rounded-lg border border-white/10 bg-white/5">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold mt-0.5">1</div>
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">Explore Your Dashboard</h4>
                        <p className="text-xs text-muted-foreground mt-1">
                          Navigate through your personalised dashboard to familiarise yourself with the layout and available features.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-3 rounded-lg border border-white/10 bg-white/5">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold mt-0.5">2</div>
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">Invite Team Members</h4>
                        <p className="text-xs text-muted-foreground mt-1">
                          Add your team members to start collaborating on documents and projects together.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-3 rounded-lg border border-white/10 bg-white/5">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold mt-0.5">3</div>
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">Create Your First Document</h4>
                        <p className="text-xs text-muted-foreground mt-1">
                          Start by creating a new document or importing existing ones to begin your journey.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 z-10 border-t border-white/10 px-6 py-4 bg-card/70 backdrop-blur-xl supports-[backdrop-filter]:bg-card/60 flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                <span className="text-green-600">Ready to start your journey</span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>View Tutorial</Button>
                <Button>Get Started</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
